import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Plus, Trash2, Search, RotateCcw, Save, Route, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CustomerSearchSelect } from "../components/CustomerSearchSelect";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID");
const BUCKETS = ["HOME", "STORE"];

export default function KeepCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [number, setNumber] = useState("—");
  const [groups, setGroups] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selCust, setSelCust] = useState(null);
  const [form, setForm] = useState({ group_id: "", customer_id: "", sales_bucket: "HOME", keep_type: "Regular", custom_cancel_at: "", marketplace: "", order_id: "", note: "" });
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [pickOpen, setPickOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data));
    api.get("/settings/marketplace-channels").then((r) => setChannels(r.data.channels || [])).catch(() => {});
    if (isEdit) {
      api.get(`/keep/${editId}`).then((r) => {
        const k = r.data;
        if (k.sales_bucket === "PRE_ORDER") { navigate(`/sales/preorder-keep/edit/${editId}`, { replace: true }); return; }
        setNumber(k.number);
        setForm({ group_id: k.group_id || "", customer_id: k.customer_id || "", sales_bucket: k.sales_bucket || "HOME",
          keep_type: k.keep_type || "Regular", marketplace: k.marketplace || "", order_id: k.order_id || "", note: k.note || "" });
        // rekonstruksi baris: gabungkan item per varian (jumlah qty lintas gudang)
        const byV = {};
        (k.items || []).forEach((it) => {
          const cur = byV[it.variant_id] || { variant_id: it.variant_id, product_name: it.product_name, color: it.color, size: it.size, price: it.price || 0, qty: 0 };
          cur.qty += parseInt(it.qty_remaining ?? it.qty) || 0;
          byV[it.variant_id] = cur;
        });
        setRows(Object.values(byV).filter((r) => r.qty > 0));
        setSelCust(k.customer_id ? { id: k.customer_id, name: k.customer_name, phone: k.phone, marketplace: k.marketplace } : null);
      }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); navigate("/sales/keep"); });
    } else {
      api.get("/keep/next-number").then((r) => setNumber(r.data.number)).catch(() => setNumber("A--"));
    }
    // eslint-disable-next-line
  }, []);

  // Prefill dari Auto Keep (order Desty): item sudah ter-mapping via Mapping Produk Marketplace
  useEffect(() => {
    const draft = location.state?.destyDraft;
    if (isEdit || !draft) return;
    setForm((f) => ({ ...f, marketplace: draft.marketplace || f.marketplace, order_id: draft.order_id || f.order_id }));
    const byV = {};
    (draft.items || []).filter((it) => it.variant_id).forEach((it) => {
      const cur = byV[it.variant_id] || { variant_id: it.variant_id, product_name: it.product_name, color: it.color, size: it.size, price: it.price || 0, qty: 0 };
      cur.qty += parseInt(it.qty) || 0;
      byV[it.variant_id] = cur;
    });
    setRows(Object.values(byV));
    const unresolved = (draft.items || []).filter((it) => !it.variant_id).length;
    if (unresolved > 0) toast.warning(`${unresolved} item order Desty belum ter-mapping — tambahkan manual.`);
    else if (Object.keys(byV).length) toast.success(`${Object.keys(byV).length} item dari order ${draft.order_id || ""} dimuat.`);
    // eslint-disable-next-line
  }, []);

  const selectedGroup = groups.find((g) => g.id === form.group_id);
  const allowedBuckets = cfg?.group?.allowed_buckets || ["HOME", "STORE", "PRE_ORDER"];

  // load routing config on group change
  useEffect(() => {
    if (!form.group_id) { setCfg(null); return; }
    api.get(`/customer-groups/${form.group_id}/routing`).then((r) => {
      setCfg(r.data);
      const def = (r.data.group?.default_sales_bucket && r.data.group.default_sales_bucket !== "PRE_ORDER") ? r.data.group.default_sales_bucket : "HOME";
      setForm((f) => ({ ...f, sales_bucket: (r.data.group?.allowed_buckets || []).includes(f.sales_bucket) ? f.sales_bucket : def }));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [form.group_id]);

  useEffect(() => {
    api.get("/customers", { params: { active: "active", group_id: form.group_id || undefined } }).then((r) => setCustomers(r.data));
  }, [form.group_id]);

  const isPO = form.sales_bucket === "PRE_ORDER";
  const bucketCfg = cfg?.buckets?.[form.sales_bucket];
  const grandTotal = useMemo(() => rows.reduce((s, r) => s + (parseInt(r.price) || 0) * (parseInt(r.qty) || 0), 0), [rows]);
  const isOnline = (selectedGroup?.name || "").toLowerCase() === "online";
  const mktOptions = Array.from(new Set([...channels, form.marketplace].filter(Boolean)));
  const [poMap, setPoMap] = useState({});

  // load Open PO quota (remaining per variant) when bucket = PRE_ORDER
  useEffect(() => {
    if (!isPO) { setPoMap({}); return; }
    api.get("/preorders/quotas", { params: { status: "Open" } })
      .then((r) => { const m = {}; (r.data.items || []).forEach((q) => { m[q.variant_id] = (m[q.variant_id] || 0) + (parseInt(q.remaining_po) || 0); }); setPoMap(m); })
      .catch(() => setPoMap({}));
  }, [isPO]);

  // live allocation preview
  useEffect(() => {
    if (!form.group_id || !rows.length) { setPreview(null); setPreviewError(null); return; }
    const items = rows.filter((r) => r.variant_id && parseInt(r.qty) > 0).map((r) => ({ variant_id: r.variant_id, qty: parseInt(r.qty), price: parseInt(r.price) || 0 }));
    if (!items.length) { setPreview(null); setPreviewError(null); return; }
    const t = setTimeout(() => {
      api.post("/keep/preview", { group_id: form.group_id, sales_bucket: form.sales_bucket, items }, { params: isEdit ? { keep_id: editId } : {} })
        .then((r) => { setPreview(r.data); setPreviewError(null); })
        .catch((e) => { setPreview(null); setPreviewError(formatApiError(e.response?.data?.detail)); });
    }, 350);
    return () => clearTimeout(t);
  }, [rows, form.group_id, form.sales_bucket]);

  const lineOf = (vid) => preview?.lines?.find((l) => l.variant_id === vid);
  const setRowQty = (v, qty) => setRows((rs) => {
    const idx = rs.findIndex((r) => r.variant_id === v.variant_id);
    if (qty <= 0) return rs.filter((r) => r.variant_id !== v.variant_id);
    if (idx === -1) return [...rs, { ...v, qty }];
    const n = [...rs]; n[idx] = { ...n[idx], qty }; return n;
  });
  const setRow = (i, patch) => { const n = [...rows]; n[i] = { ...n[i], ...patch }; setRows(n); };
  const reset = () => { setForm({ group_id: "", customer_id: "", sales_bucket: "HOME", keep_type: "Regular", custom_cancel_at: "", marketplace: "", order_id: "", note: "" }); setRows([]); setPreview(null); setPreviewError(null); setSelCust(null); };

  const save = async () => {
    if (saving) return;
    if (!form.group_id) return toast.error("Pilih Customer Group dulu");
    if (!form.customer_id || !selCust) return toast.error("Pilih customer dulu");
    if (previewError) return toast.error(previewError);
    const items = rows.filter((r) => r.variant_id && parseInt(r.qty) > 0).map((r) => ({ variant_id: r.variant_id, qty: parseInt(r.qty) || 0, price: parseInt(r.price) || 0 }));
    if (!items.length) return toast.error("Tambahkan minimal satu produk");
    if (preview && preview.lines.some((l) => !l.ok)) return toast.error("Ada item yang tidak dapat dialokasikan. Perbaiki qty/routing.");
    const cust = selCust;
    const payload = {
      group_id: form.group_id, group_name: selectedGroup?.name || "", sales_bucket: form.sales_bucket,
      customer_id: form.customer_id || null, customer_name: cust?.name || "", phone: cust?.phone || "",
      keep_type: form.keep_type, marketplace: form.marketplace || "", order_id: form.order_id || "",
      custom_cancel_at: form.keep_type === "Custom" && form.custom_cancel_at ? new Date(form.custom_cancel_at).toISOString() : null,
      note: form.note, items,
    };
    if (isOnline && !form.marketplace) return toast.error("Pilih Marketplace untuk Group Online");
    setSaving(true);
    try {
      const r = isEdit ? await api.put(`/keep/${editId}`, payload) : await api.post("/keep", payload);
      toast.success(isEdit ? `Keep ${r.data.number} diperbarui` : `Keep ${r.data.number} dibuat & aktif`);
      navigate("/sales/keep");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div data-testid="keep-create-page">
      <PageHeader title={isEdit ? "Edit Keep" : "Create Keep"} subtitle="Alokasi otomatis berdasarkan routing Customer Group (HOME/STORE/PRE_ORDER)" />

      <Panel className="p-5 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="text-sm text-cocoa">No Keep : <span className="font-mono font-bold text-status-passed" data-testid="keep-number">{number}</span></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Group</Label>
            <Select value={form.group_id || ""} onValueChange={(v) => { setForm({ ...form, group_id: v, customer_id: "" }); setSelCust(null); }}>
              <SelectTrigger className="bg-white" data-testid="keep-group-select"><SelectValue placeholder="Select Group" /></SelectTrigger>
              <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Customer</Label>
            <CustomerSearchSelect groupId={form.group_id} selected={selCust} testid="keep-customer-search"
              onSelect={(c) => { setSelCust(c); setForm((f) => ({ ...f, customer_id: c.id, marketplace: (isOnline && c.marketplace) ? c.marketplace : f.marketplace })); }}
              onClear={() => { setSelCust(null); setForm((f) => ({ ...f, customer_id: "" })); }} />
          </div>
          <div>
            <Label>Sales Bucket</Label>
            <Select value={form.sales_bucket} onValueChange={(v) => setForm({ ...form, sales_bucket: v })}>
              <SelectTrigger className="bg-white" data-testid="keep-bucket-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BUCKETS.map((b) => {
                  const dis = !!cfg && !allowedBuckets.includes(b);
                  return <SelectItem key={b} value={b} disabled={dis} data-testid={`keep-bucket-opt-${b}`}>{b}{dis ? " (belum diizinkan)" : ""}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            <p className="text-xs text-wmuted mt-1">Untuk <b>Pre-Order</b>, gunakan halaman khusus <button type="button" onClick={() => navigate("/sales/preorder-keep-create")} className="text-cocoa underline font-medium" data-testid="keep-goto-po">Create Keep PO</button> (memakai kuota PO, bukan stok fisik).</p>
          </div>
          <div>
            <Label>Keep Type</Label>
            <Select value={form.keep_type} onValueChange={(v) => setForm({ ...form, keep_type: v })}>
              <SelectTrigger className="bg-white" data-testid="keep-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Regular">Regular (auto-cancel sesuai setting)</SelectItem>
                <SelectItem value="Custom">Custom (pilih waktu auto-cancel)</SelectItem>
              </SelectContent>
            </Select>
            {form.keep_type === "Custom" && (
              <Input type="datetime-local" value={form.custom_cancel_at} onChange={(e) => setForm({ ...form, custom_cancel_at: e.target.value })} className="bg-white mt-2" data-testid="keep-custom-cancel-input" />
            )}
          </div>
        </div>

        {/* Dynamic routing info panel */}
        {cfg && (
          <div className="mt-4 rounded-lg border border-taupe/25 bg-ivory/60 p-3 text-sm" data-testid="keep-routing-info">
            {isPO ? (
              <div className="flex items-start gap-2 text-blue-900">
                <Info className="w-4 h-4 mt-0.5 text-blue-600" />
                <div><b>Bucket: PRE_ORDER</b> — tidak mengambil stok fisik. Alokasi memakai <b>Remaining PO</b> (kuota). {!allowedBuckets.includes("PRE_ORDER") && <span className="text-amber-700">PRE_ORDER belum diizinkan untuk group ini.</span>}</div>
              </div>
            ) : !bucketCfg?.allowed ? (
              <div className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-4 h-4" />Bucket <b>{form.sales_bucket}</b> belum diizinkan untuk group ini.</div>
            ) : !bucketCfg?.warehouses?.length ? (
              <div className="flex items-center gap-2 text-amber-700"><AlertTriangle className="w-4 h-4" />Routing <b>{form.sales_bucket}</b> belum dikonfigurasi. Atur di Customer Group → Routing.</div>
            ) : (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-cocoa">
                <span><b>Bucket:</b> {form.sales_bucket}</span>
                <span className="flex items-center gap-1"><Route className="w-4 h-4 text-taupe" /><b>Routing:</b> {bucketCfg.warehouses.map((w) => w.warehouse_name).join(" → ")}</span>
                <span><b>Split Fulfillment:</b> {cfg.group.allow_split_fulfillment ? <span className="text-status-passed">Aktif</span> : <span className="text-wmuted">Nonaktif</span>}</span>
                <span data-testid="keep-bucket-available"><b>{form.sales_bucket} Available:</b> <span className="text-status-passed font-bold">{num(bucketCfg.total_available)}</span></span>
              </div>
            )}
          </div>
        )}

        {(
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="keep-marketplace-fields">
            <div>
              <Label>Marketplace</Label>
              <Select value={form.marketplace || ""} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
                <SelectTrigger className="bg-white" data-testid="keep-marketplace-select"><SelectValue placeholder="Select Marketplace" /></SelectTrigger>
                <SelectContent>{mktOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Order ID Marketplace</Label><Input value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="Enter Order ID" className="bg-white" data-testid="keep-order-id-input" /></div>
          </div>
        )}

        <div className="mt-4"><Label>Keep Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="keep-note-input" /></div>
      </Panel>

      <Panel className="mb-4">
        {previewError && (
          <div className="m-3 mb-0 rounded-lg border border-status-reject/40 bg-status-reject/5 p-3 text-sm flex items-start gap-2 text-status-reject" data-testid="keep-preview-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <b>Tidak bisa mengalokasikan:</b> {previewError}
              {isPO && /diizinkan/i.test(previewError) && (
                <div className="text-xs text-cocoa mt-1">Aktifkan bucket <b>PRE_ORDER</b> untuk group ini di menu <b>Customer → Group → Edit → "Bucket yang Diizinkan"</b>, centang PRE_ORDER, lalu simpan.</div>
              )}
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead>
              <TableHead>Product Name</TableHead><TableHead>Color</TableHead><TableHead>Size</TableHead>
              <TableHead>{isPO ? "Remaining PO" : "Alokasi Gudang"}</TableHead>
              <TableHead className="text-right">Price (@)</TableHead>
              <TableHead className="text-center w-24">Qty</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const line = lineOf(r.variant_id);
              return (
                <TableRow key={r.variant_id} data-testid={`keep-row-${i}`} className={line && !line.ok ? "bg-status-reject/5" : ""}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-medium text-cocoa">{r.product_name}</TableCell>
                  <TableCell>{r.color}</TableCell>
                  <TableCell>{r.size}</TableCell>
                  <TableCell data-testid={`keep-alloc-${i}`}>
                    {isPO ? (
                      <span className="text-sm">Sisa PO: <b>{line ? num(line.remaining_po) : "…"}</b></span>
                    ) : line ? (
                      line.ok ? (
                        <div className="flex flex-col gap-0.5 text-xs">
                          {line.allocations.filter((a) => a.qty > 0).map((a) => (
                            <span key={a.warehouse_id}><span className="text-cocoa font-medium">{a.warehouse_name}</span>: <b className="text-status-passed">{a.qty}</b></span>
                          ))}
                        </div>
                      ) : <span className="text-xs text-status-reject flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{line.error}</span>
                    ) : <span className="text-xs text-wmuted">menghitung…</span>}
                  </TableCell>
                  <TableCell className="text-right"><Input type="number" className="w-28 text-right ml-auto" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`keep-row-price-${i}`} /></TableCell>
                  <TableCell className="text-center"><Input type="number" className="w-20 text-center mx-auto" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`keep-row-qty-${i}`} /></TableCell>
                  <TableCell className="text-right font-medium">{fmtIDR((parseInt(r.price) || 0) * (parseInt(r.qty) || 0))}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`keep-row-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button></TableCell>
                </TableRow>
              );
            })}
            {!rows.length && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-8">Belum ada produk.</TableCell></TableRow>}
          </TableBody>
        </Table>
        <div className="flex items-center justify-center py-4 border-t border-taupe/15">
          <Button onClick={() => { if (!form.group_id) return toast.error("Pilih Group dulu"); setPickOpen(true); }} data-testid="add-product-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> Add Product</Button>
        </div>
      </Panel>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-cocoa">Grand Total: <span className="font-bold text-lg">{fmtIDR(grandTotal)}</span></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={reset} className="border-status-reject/40 text-status-reject" data-testid="keep-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="save-keep-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : (isEdit ? "Update Keep" : "Save Keep")}</Button>
        </div>
      </div>

      <ProductPicker open={pickOpen} setOpen={setPickOpen} rows={rows} onSetQty={setRowQty} isPO={isPO} poMap={poMap} />
    </div>
  );
}

function ProductPicker({ open, setOpen, rows, onSetQty, isPO, poMap }) {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async (term) => {
    setLoading(true);
    try {
      const r = await api.get("/keep/products", { params: { q: term } });
      setProducts((r.data.products || []).map((p) => ({ ...p, variants: (p.variants || []).map((v) => ({ ...v, total: (v.warehouses || []).reduce((s, w) => s + (w.available || 0), 0) })) })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setQ(""); setProducts([]); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setProducts([]); return; }
    const t = setTimeout(() => doSearch(term), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, open]);
  const qtyOf = (vid) => rows.find((r) => r.variant_id === vid)?.qty || 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Add Product</DialogTitle></DialogHeader>
        <div className="mb-3">
          <Label className="text-xs">Product</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik nama produk / SKU (otomatis)..." className="pl-9 bg-white" data-testid="product-search-input" autoFocus />
            {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-wmuted">Mencari...</span>}
          </div>
          <p className="text-xs text-wmuted mt-1">{isPO ? "Mode PRE_ORDER: hanya varian dengan kuota PO (Open) yang tampil. Angka = Sisa PO (kuota), bukan stok fisik." : "Total Stock = stok gabungan semua gudang (referensi). Alokasi aktual mengikuti routing bucket."}</p>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Color</TableHead><TableHead>Size</TableHead><TableHead className="text-center">{isPO ? "Sisa PO" : "Total Stock"}</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-center w-44">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => {
              const vlist = isPO ? p.variants.filter((v) => (poMap?.[v.variant_id] || 0) > 0) : p.variants;
              if (isPO && !vlist.length) return null;
              return (
              <React.Fragment key={p.product_id}>
                <TableRow className="hover:bg-transparent bg-taupe/10"><TableCell colSpan={5} className="font-semibold text-cocoa py-2" data-testid={`pick-product-${p.product_id}`}>{p.product_name}</TableCell></TableRow>
                {vlist.map((v) => {
                  const cur = qtyOf(v.variant_id);
                  const avail = isPO ? (poMap?.[v.variant_id] || 0) : v.total;
                  const outOfStock = avail <= 0;
                  const vobj = { variant_id: v.variant_id, product_id: p.product_id, product_name: p.product_name, color: v.color, size: v.size, price: v.price, available: avail };
                  return (
                    <TableRow key={v.variant_id} data-testid={`pick-row-${v.variant_id}`} className={outOfStock ? "opacity-60" : ""}>
                      <TableCell className="text-sm">{v.color}{v.borrowed_flag ? <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-testid={`pick-borrowed-${v.variant_id}`}>{v.borrowed_flag} dipinjam</span> : null}</TableCell>
                      <TableCell className="text-sm">{v.size}</TableCell>
                      <TableCell className={`text-center font-medium ${outOfStock ? "text-status-reject" : (isPO ? "text-blue-700" : "")}`}>{outOfStock ? "Habis" : avail}</TableCell>
                      <TableCell className="text-right text-sm">{fmtIDR(v.price)}</TableCell>
                      <TableCell>
                        {outOfStock ? (
                          <div className="text-center text-xs text-status-reject font-medium" data-testid={`pick-outofstock-${v.variant_id}`}>Stok habis</div>
                        ) : (
                        <div className="flex items-center justify-center gap-1">
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" disabled={cur <= 0} onClick={() => onSetQty(vobj, Math.max(0, cur - 1))} data-testid={`pick-minus-${v.variant_id}`}>−</Button>
                          <Input type="number" className="w-16 h-8 text-center" value={cur || ""} onChange={(e) => onSetQty(vobj, Math.max(0, Math.min(parseInt(e.target.value) || 0, avail)))} data-testid={`pick-qty-${v.variant_id}`} />
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" disabled={cur >= avail} onClick={() => onSetQty(vobj, Math.min(cur + 1, avail))} data-testid={`pick-plus-${v.variant_id}`}>+</Button>
                        </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
              );
            })}
            {!products.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">{loading ? "Mencari..." : (isPO ? "Ketik nama produk PO (min 2 huruf). Hanya varian dengan kuota PO Open yang tampil." : "Ketik minimal 2 huruf, hasil tampil otomatis.")}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <DialogFooter><Button onClick={() => setOpen(false)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="pick-done-btn">Selesai</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
